// src/pages/PublicSector/Institutions.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../../components/SEO";

const BASE_URL = process.env.REACT_APP_BASE_URL || "https://myboq.in";
const WEBSITE_NAME = process.env.REACT_APP_WEBSITE_NAME || "myBOQ";

// ---- CLARITY helper ----
const track = (name, props = {}) => {
  if (typeof window !== "undefined" && window.clarity) {
    window.clarity("event", name, props);
  }
};

const Institutions = () => {
  useEffect(() => {
    track("public_sector_institutions_view", { path: "/public-sector/institutions" });
  }, []);

  return (
    <main className="bg-white">
      <SEO
        title={`${WEBSITE_NAME} | Institutions & Training Partnership`}
        description="Institution-ready SSR estimation training & adoption model with myBOQ and CEIIT. Structured programs, practical workflows, and certification."
        keywords="myBOQ institutions, civil estimation training, SSR training, Maharashtra PWD SSR, CEIIT training partner, government estimation course"
        image={`${BASE_URL}/ssrlogo3.png`}
        url={`${BASE_URL}/public-sector/institutions`}
      />

      {/* HERO */}
      <section className="px-5 sm:px-6 lg:px-20 pt-10 pb-10">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <div className="text-sm text-gray-500">
            <Link
              to="/"
              className="hover:text-orange-600"
              onClick={() => track("nav_click", { item: "home", from: "institutions_breadcrumb" })}
            >
              Home
            </Link>
            <span className="mx-2">/</span>
            <Link
              to="/public-sector/use-cases"
              className="hover:text-orange-600"
              onClick={() => track("nav_click", { item: "public_sector", from: "institutions_breadcrumb" })}
            >
              Public Sector
            </Link>
            <span className="mx-2">/</span>
            <span className="text-gray-700 font-semibold">Institutions</span>
          </div>

          <div className="mt-4 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 leading-tight">
                Institution-Ready Estimation Training & Certification
              </h1>

              <p className="mt-4 text-gray-700 text-base sm:text-lg leading-relaxed">
                myBOQ supports institutions (colleges, training centers, industry programs, and public
                learning initiatives) with structured{" "}
                <span className="font-semibold text-gray-900">SSR-based Government Estimation</span>{" "}
                training — aligned to real workflows used by engineers, contractors, and consultants.
              </p>

              <p className="mt-3 text-gray-600">
                Built for Maharashtra today (MH PWD SSR), with a roadmap to expand into more
                departments and states.
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center rounded-full bg-orange-500 px-6 py-3 text-white font-semibold hover:bg-orange-600 transition"
                  onClick={() => track("apply_cta_click", { placement: "institutions_hero", target: "signup" })}
                >
                  Get Started Free
                </Link>

                <a
                  href="https://civilengineeringiit.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-full bg-gray-100 px-6 py-3 text-gray-900 font-semibold hover:bg-gray-200 transition"
                  onClick={() => track("partner_visit", { name: "CEIIT", placement: "institutions_hero" })}
                >
                  Visit CEIIT
                </a>
              </div>

              {/* Small trust chips */}
              <div className="mt-6 flex flex-wrap gap-2">
                {[
                  "SSR-based practical training",
                  "Certificate on completion",
                  "Recorded + Live programs",
                  "Institution collaboration model",
                ].map((chip) => (
                  <span
                    key={chip}
                    className="text-xs sm:text-sm px-3 py-1 rounded-full bg-orange-50 text-orange-800 border border-orange-100"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>

            {/* Right card */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center">
                    <span className="text-xl" aria-hidden="true">
                      🎓
                    </span>
                  </div>

                  <div>
                    <div className="text-gray-900 font-extrabold text-lg">What institutions get</div>
                    <p className="text-gray-600 text-sm mt-1">
                      A packaged training + workflow adoption model that increases employability and
                      improves estimation quality.
                    </p>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  {[
                    "Curriculum & training delivery",
                    "Hands-on myBOQ workflow practice",
                    "Reference estimate sheets",
                    "Certificate-based validation",
                  ].map((item) => (
                    <div key={item} className="rounded-xl border border-gray-200 p-3 bg-gray-50">
                      <span className="text-gray-900 font-semibold">✔</span>{" "}
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-5 text-xs text-gray-500">
                  Note: Programs can be offered to students, professionals, and institutional cohorts.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRAINING PROGRAMS */}
      <section className="px-5 sm:px-6 lg:px-20 pb-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
              Government Estimation Training Programs
            </h2>
            <div className="text-sm text-gray-500">
              Delivered using real SSR workflows on myBOQ
            </div>
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-6">
            {/* Recorded */}
            <div className="rounded-2xl border border-orange-200 bg-orange-50 p-6 shadow-sm">
  <div className="flex items-start justify-between gap-3">
    <div className="flex items-start gap-3">
      <div className="h-11 w-11 rounded-xl bg-white/70 border border-orange-100 flex items-center justify-center">
        <span className="text-xl" aria-hidden="true">
          ▶️
        </span>
      </div>

      <div>
        <h3 className="text-lg font-extrabold text-gray-900">
          Recorded Government Estimation Training
        </h3>
        <p className="mt-1 text-sm text-gray-700">
          Video-based structured modules delivered through the myBOQ platform.
        </p>
      </div>
    </div>

    <span className="inline-flex items-center rounded-full bg-white border border-orange-200 px-3 py-1 text-xs font-semibold text-orange-700 whitespace-nowrap">
      Coming Soon
    </span>
  </div>

  <ul className="mt-4 text-sm text-gray-700 space-y-2 list-disc list-inside">
    <li>Step-by-step SSR estimate preparation</li>
    <li>Rate analysis breakdown explanation</li>
    <li>Lead calculation training</li>
    <li>Certificate upon successful completion</li>
  </ul>

  <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
    <div className="font-extrabold text-orange-700">Training Fee: ₹999</div>

    <button
      type="button"
      disabled
      className="inline-flex items-center justify-center rounded-full bg-orange-200 px-5 py-2 text-white text-sm font-semibold cursor-not-allowed opacity-80"
    >
      Coming Soon
    </button>
  </div>
</div>

            {/* Live */}
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center">
                  <span className="text-xl" aria-hidden="true">
                    🧑‍🏫
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-extrabold text-gray-900">10-Day Live Professional Training</h3>
                  <p className="mt-1 text-sm text-gray-700">
                    Instructor-led structured estimation training delivered by CEIIT.
                  </p>
                </div>
              </div>

              <ul className="mt-4 text-sm text-gray-700 space-y-2 list-disc list-inside">
                <li>10 days live structured sessions</li>
                <li>Hands-on SSR estimate preparation</li>
                <li>
                  <span className="font-semibold text-gray-900">1-Year myBOQ Validity</span> included
                </li>
                <li>Excel sheets of 10 reference estimates</li>
                <li>Certificate after completion</li>
              </ul>

              <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="font-extrabold text-gray-900">Training Fee: ₹5900</div>
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center rounded-full bg-gray-900 px-5 py-2 text-white text-sm font-semibold hover:bg-black transition"
                  onClick={() =>
                    track("training_interest_click", {
                      placement: "institutions_training",
                      mode: "live",
                      price: 5900,
                      partner: "CEIIT",
                    })
                  }
                >
                  Enroll Now
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CEIIT PARTNER CARD */}
      <section className="px-5 sm:px-6 lg:px-20 py-10">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 sm:p-8">
            <div className="flex flex-col md:flex-row items-start gap-6">
              {/* CEIIT Logo */}
              <div className="flex-shrink-0">
                <img src="/CEIIT_LOGO.png" alt="CEIIT logo" className="w-28 h-auto object-contain" />
              </div>

              {/* Content */}
              <div>
                <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900">
                  Training Delivered by CEIIT
                </h2>

                <p className="mt-3 text-gray-600 leading-relaxed">
                  CEIIT (Civil Engineering Industry &amp; Infrastructure Training) is an official
                  training partner collaborating with myBOQ to deliver structured Government
                  Estimation Training programs.
                </p>

                <p className="mt-3 text-gray-600 leading-relaxed">
                  Through this partnership, institutions and learners gain access to practical
                  SSR-based estimation training using real myBOQ workflows — bridging academic
                  learning with real-world infrastructure practices.
                </p>

                <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm text-gray-700">
                  <div className="rounded-xl border border-gray-200 p-3 bg-gray-50">
                    ✔ Structured government estimation curriculum
                  </div>
                  <div className="rounded-xl border border-gray-200 p-3 bg-gray-50">
                    ✔ Live &amp; recorded training programs
                  </div>
                  <div className="rounded-xl border border-gray-200 p-3 bg-gray-50">
                    ✔ Practical SSR estimate preparation
                  </div>
                  <div className="rounded-xl border border-gray-200 p-3 bg-gray-50">
                    ✔ Certificate issued after completion
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap gap-3">
                  <a
                    href="https://civilengineeringiit.in/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center rounded-full bg-orange-500 px-5 py-2 text-white text-sm font-semibold hover:bg-orange-600 transition"
                    onClick={() => track("partner_visit", { name: "CEIIT", placement: "institutions_partner_card" })}
                  >
                    Visit CEIIT
                  </a>

                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center rounded-full bg-gray-100 px-5 py-2 text-gray-900 text-sm font-semibold hover:bg-gray-200 transition"
                    onClick={() => track("contact_click", { placement: "institutions_partner_card" })}
                  >
                    Contact myBOQ
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INSTITUTIONAL MODEL */}
      <section className="px-5 sm:px-6 lg:px-20 pb-14">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-2xl bg-[#0B1B3A] text-white p-7 sm:p-10 shadow-md">
            <h2 className="text-2xl sm:text-3xl font-extrabold">Institutional Collaboration Model</h2>

            <p className="mt-3 text-white/80 leading-relaxed">
              Institutions can partner with CEIIT and myBOQ to deliver structured Government Estimation
              Training programs integrated with real software workflows.
            </p>

            <div className="mt-6 grid sm:grid-cols-3 gap-4 text-sm">
              <div className="bg-white/10 p-4 rounded-xl border border-white/10">
                Digital-first training delivery
              </div>
              <div className="bg-white/10 p-4 rounded-xl border border-white/10">
                Industry-aligned curriculum
              </div>
              <div className="bg-white/10 p-4 rounded-xl border border-white/10">
                Certification-based skill validation
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/10 px-4 py-2 text-sm">
                <span aria-hidden="true">✅</span>
                Learn → Practice → Generate SSR Reports → Get Certified
              </div>

              <div className="text-sm text-orange-300 font-semibold">
                Maharashtra focused today • Multi-state roadmap coming soon
              </div>
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center rounded-full bg-orange-500 px-6 py-3 text-white font-semibold hover:bg-orange-600 transition"
                onClick={() => track("apply_cta_click", { placement: "institutions_footer", target: "signup" })}
              >
                Get Started Free
              </Link>

              <Link
                to="/public-sector/compliance"
                className="inline-flex items-center justify-center rounded-full bg-white/10 px-6 py-3 text-white font-semibold hover:bg-white/20 transition"
                onClick={() => track("nav_click", { item: "public_sector_compliance", placement: "institutions_footer" })}
              >
                View Compliance & Standardization →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Institutions;